// src/pages/skilledWorkerRedundancy.js

const skilledWorkerRedundancy = {
    slug: "skilled-worker-redundancy",

    title: "Skilled Worker Visa Redundancy: What Happens to Your Visa",

    seo: {
        metaTitle:
            "Skilled Worker Visa Redundancy: The 60-Day Rule Explained",
        metaDescription:
            "Made redundant or your sponsorship ended on a Skilled Worker visa? Understand the 60-day rule, your employer's reporting duty, and your three options.",
        keywords: [
            "Skilled Worker visa redundancy",
            "60 day visa rule",
            "sponsorship ended visa",
            "Skilled Worker visa job loss"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Skilled Worker Redundancy",
            url: "/skilled-worker-redundancy"
        }
    ],

    introduction: `
If your [Skilled Worker](https://www.gov.uk/skilled-worker-visa) sponsorship has
ended, whether because of redundancy, your job ending, or your employer losing
its sponsor licence, you are on a strict clock. In most cases you will be given
60 days of permission to stay in the UK to sort out your next step, but during
that time you cannot work.

IMPORTANT DISCLAIMER: This page explains the general 60-day process based on
published Home Office guidance. It is not legal advice and it cannot account
for your specific circumstances, dates, or immigration history: details that
can change the analysis significantly. Given how time-sensitive this
situation is and how serious the consequences of getting it wrong can be
(including risking an overstay), we strongly recommend speaking with a
qualified immigration solicitor as soon as possible if this applies to you.
`,

    sections: [
        {
            heading: "The 60-Day Rule",
            content: `
When a Skilled Worker's sponsorship ends before the end date on their visa,
for example because of redundancy, the job ending, or the sponsor losing its
licence, the Home Office will normally curtail (cut short) the visa. In most
cases where the worker was not at fault, this means limiting permission to
stay to 60 days from the date the sponsorship ends (or however long is left
on the visa, if that is less than 60 days).

This is confirmed on [GOV.UK's guidance for employees whose sponsor loses its
licence](https://www.gov.uk/employee-lose-sponsor-licence), and in the Home
Office's own caseworker guidance on cancellation and curtailment of
permission, which states that it will "normally be appropriate" to leave
someone with 60 days' permission so they can either apply for further
permission to stay or make arrangements to leave the UK.

During those 60 days you remain in the UK lawfully, but you cannot work.
This applies whether it would be for your former employer or anyone else,
until you have a new, valid immigration status that permits it.
`
        },
        {
            heading: "The Employer's Reporting Duty",
            content: `
Sponsors have their own legal duty in this situation. Under their sponsor
licence obligations, employers must report to UKVI within 10 working days
of certain events, including when a sponsored worker's contract of
employment ends earlier than the date shown on their Certificate of
Sponsorship (CoS).

This reporting duty matters to you directly:

- It is generally what starts the process that leads to your visa being
  curtailed and the 60-day clock being set
- Delays or failures by an employer to report on time do not change the
  underlying facts of when your sponsorship actually ended
- It's worth finding out from your employer (or former employer) exactly
  when they notified, or plan to notify, UKVI
`
        },
        {
            heading: "Your Three Options",
            content: `
Broadly, if your sponsorship has ended, you have three options during your
60-day period:

1. **Find a new licensed sponsor.** You can look for a new employer who
   holds a Skilled Worker sponsor licence and is willing to assign you a
   fresh Certificate of Sponsorship, then apply for a new Skilled Worker
   visa before your current permission runs out.

2. **Switch to a different eligible visa category.** Depending on your
   circumstances, you may be eligible to switch into a different UK visa
   route.

3. **Leave the UK.** If neither of the above is realistic within your
   remaining time, you will need to leave the UK before your permission
   expires to avoid overstaying.

Which option makes sense depends heavily on your personal circumstances,
timing, and immigration history. This is exactly the kind of decision
where getting tailored advice quickly matters.
`
        },
        {
            heading: "What About Your Dependants?",
            content: `
If you have dependants on your Skilled Worker visa, their permission to stay
is tied to yours. When your visa is curtailed to 60 days, your dependants'
visas are curtailed in parallel, also to 60 days. There is no independent
grace period for dependants: they need to switch to a new valid status or
leave the UK alongside you.
`
        },
        {
            heading: "Where This Gets Case-Specific",
            content: `
This page deliberately does not go into detail on several areas, because
getting them wrong can have serious consequences and the correct answer
depends on facts specific to you:

- **Switching to a different occupation or SOC code.** Eligibility rules
  here are detailed and change over time, so what was allowed previously may
  not apply to your situation now.
- **The redundancy and consultation process itself** (including ACAS
  guidance on employment rights). This is an employment law question distinct
  from your immigration status, with its own rules and timelines.
- **Dependant edge cases** beyond the general rule above, for example where
  a dependant has independent grounds to stay, or a separate application
  is already in progress.

If any of these apply to you, please treat this as a signal to get advice
from a qualified immigration solicitor rather than trying to work out the
mechanics yourself from general guidance.
`,
            solicitorCta: true
        }
    ],

    faqs: [
        {
            question: "How long do I have if my Skilled Worker sponsorship ends?",
            answer:
                "In most cases, you will be given 60 days of permission to stay in the UK from when your sponsorship ends (or less if your visa was due to expire sooner). During this time you cannot work."
        },
        {
            question: "Can I work during the 60-day period?",
            answer:
                "No. You remain in the UK lawfully during the 60 days, but you are not permitted to work for anyone until you have valid immigration permission that allows it."
        },
        {
            question: "How soon does my employer have to report that my job has ended?",
            answer:
                "Sponsors must report certain changes, including a sponsored worker's contract ending earlier than the date on their Certificate of Sponsorship, to UKVI within 10 working days."
        },
        {
            question: "What happens to my dependants' visas?",
            answer:
                "Your dependants' visas are curtailed in parallel with yours, also to 60 days. They do not get an independent grace period and need to switch status or leave the UK alongside you."
        },
        {
            question: "Can I switch to a different job type or occupation code?",
            answer:
                "This is genuinely case-specific and depends on rules that change over time, including which occupation codes are currently eligible. We don't attempt to explain the mechanics on this page: please speak with a qualified immigration solicitor about your specific situation."
        },
        {
            question: "What if I think my employer hasn't reported my job ending?",
            answer:
                "This is a situation where the exact timing and facts matter a great deal. We'd strongly recommend speaking with a qualified immigration solicitor promptly rather than waiting to find out."
        }
    ],

    relatedPages: [
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "Skilled Worker ILR Guide",
            url: "/skilled-worker-ilr"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Employees: if your visa sponsor loses their licence",
            url: "https://www.gov.uk/employee-lose-sponsor-licence"
        },
        {
            title: "Home Office - Cancellation and curtailment of permission (caseworker guidance)",
            url: "https://www.gov.uk/government/publications/immigration-status-and-enforcement-action-caseworker-guidance/cancellation-and-curtailment-of-permission-accessible"
        },
        {
            title: "Home Office - Workers and Temporary Workers: sponsor duties and compliance",
            url: "https://www.gov.uk/government/publications/workers-and-temporary-workers-guidance-for-sponsors-part-3-sponsor-duties-and-compliance/workers-and-temporary-workers-guidance-for-sponsors-part-3-sponsor-duties-and-compliance-accessible"
        },
        {
            title: "GOV.UK - Skilled Worker Visa",
            url: "https://www.gov.uk/skilled-worker-visa"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-31"
    }
};

export default skilledWorkerRedundancy;
